/**
 * Shared Device Offline Store
 *
 * IndexedDB storage for offline shared-device operation:
 * - Database per device: `shared-device-${deviceId}`
 * - Per-device PBKDF2 salt generated once and stored
 * - PBKDF2-SHA-256 PIN verifiers (200k iterations, salt = device salt + uid)
 * - Offline lockout (5 wrong attempts in 15 min locks for 15 min)
 * - Cached people list (names and initials only)
 * - Offline session tracking (requires verification within last 60s)
 */

import {
  SHARED_DEVICE_LOCKOUT_DURATION_MS,
  SHARED_DEVICE_LOCKOUT_MAX_ATTEMPTS,
  SHARED_DEVICE_LOCKOUT_WINDOW_MS,
  SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS,
} from '../types/ops'

const DB_VERSION = 1
const STORE_META = 'meta'
const STORE_VERIFIERS = 'verifiers'
const STORE_LOCKOUTS = 'lockouts'
const STORE_SESSIONS = 'sessions'
const STORE_RECENT_VERIFICATIONS = 'recent_verifications'

export const PBKDF2_ITERATIONS = 200000
export const PBKDF2_BITS = 256
export const VERIFIER_WINDOW_MS = SHARED_DEVICE_OFFLINE_MAX_WINDOW_MS
export const VERIFICATION_VALIDITY_WINDOW_MS = 60 * 1000 // 60 seconds

export interface CachedPerson {
  uid: string
  name: string
  initials: string
}

export interface CachedPeopleRecord {
  cachedAt: number
  people: CachedPerson[]
}

export interface OfflineEligiblePerson extends CachedPerson {
  offlineEligible: boolean
}

export interface StoredVerifier {
  uid: string
  hash: string
  iterations: number
  expiresAt: number
}

export interface LockoutRecord {
  uid: string
  attempts: number[]
  lockedUntil?: number
}

export interface StoredOfflineSession {
  offlineSessionId: string
  uid: string
  startedAt: number
  endedAt?: number
  endReason?: string
}

export type OfflinePinVerifyResult = 'OK' | 'WRONG' | 'EXPIRED' | 'NO_VERIFIER' | 'LOCKED'

// Cache open DB promises per deviceId
const dbCache = new Map<string, Promise<IDBDatabase>>()

const openDeviceDb = (deviceId: string): Promise<IDBDatabase> => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'))
  }
  const cached = dbCache.get(deviceId)
  if (cached) return cached

  const dbName = `shared-device-${deviceId}`
  const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains(STORE_VERIFIERS)) {
        db.createObjectStore(STORE_VERIFIERS, { keyPath: 'uid' })
      }
      if (!db.objectStoreNames.contains(STORE_LOCKOUTS)) {
        db.createObjectStore(STORE_LOCKOUTS, { keyPath: 'uid' })
      }
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'offlineSessionId' })
      }
      if (!db.objectStoreNames.contains(STORE_RECENT_VERIFICATIONS)) {
        db.createObjectStore(STORE_RECENT_VERIFICATIONS, { keyPath: 'uid' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IDB open failed'))
  })

  dbCache.set(deviceId, dbPromise)
  return dbPromise
}

const runTx = async <T>(
  deviceId: string,
  storeNames: string | string[],
  mode: IDBTransactionMode,
  work: (stores: (name: string) => IDBObjectStore) => IDBRequest<T> | Promise<T>,
): Promise<T> => {
  const db = await openDeviceDb(deviceId)
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeNames, mode)
    const storeLookup = (name: string) => tx.objectStore(name)
    let resultPromise: Promise<T> | IDBRequest<T>
    try {
      resultPromise = work(storeLookup)
    } catch (err) {
      reject(err)
      return
    }

    if (resultPromise instanceof Promise) {
      resultPromise.then(
        (res) => {
          tx.oncomplete = () => resolve(res)
        },
        (err) => reject(err),
      )
    } else {
      resultPromise.onsuccess = () => {
        resolve(resultPromise.result)
      }
      resultPromise.onerror = () => reject(resultPromise.error ?? new Error('Transaction request failed'))
    }

    tx.onerror = () => reject(tx.error ?? new Error('Transaction failed'))
  })
}

/**
 * Gets or creates the per-device 16-byte random salt.
 */
export async function getOrCreateDeviceSalt(deviceId: string): Promise<Uint8Array> {
  const existing = await runTx(deviceId, STORE_META, 'readonly', (store) => {
    return store(STORE_META).get('device_salt')
  })
  if (existing?.value instanceof Uint8Array && existing.value.length === 16) {
    return existing.value
  }

  // Generate 16 random bytes
  const newSalt = crypto.getRandomValues(new Uint8Array(16))
  await runTx(deviceId, STORE_META, 'readwrite', (store) => {
    return store(STORE_META).put({ key: 'device_salt', value: newSalt })
  })
  return newSalt
}

/**
 * Constant-time string comparison to prevent timing side-channels.
 */
export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  }
  return diff === 0
}

/**
 * Derives a PBKDF2-SHA-256 hash using crypto.subtle.
 * Salt = device salt + uid. Iterations = 200,000. Output = 256 bits (32 bytes hex).
 */
export async function derivePinHash(deviceId: string, uid: string, pin: string): Promise<string> {
  const deviceSalt = await getOrCreateDeviceSalt(deviceId)
  const uidBytes = new TextEncoder().encode(uid)
  const combinedSalt = new Uint8Array(deviceSalt.length + uidBytes.length)
  combinedSalt.set(deviceSalt, 0)
  combinedSalt.set(uidBytes, deviceSalt.length)

  const pinBytes = new TextEncoder().encode(pin)
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    pinBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveBits'],
  )

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: combinedSalt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    PBKDF2_BITS,
  )

  return Array.from(new Uint8Array(derivedBits))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Stores a PIN verifier for a person after a successful online switch-in.
 */
export async function storeOfflinePinVerifier(
  deviceId: string,
  uid: string,
  pin: string,
  lastOnlineSwitchInAt: number,
): Promise<void> {
  const hash = await derivePinHash(deviceId, uid, pin)
  const expiresAt = lastOnlineSwitchInAt + VERIFIER_WINDOW_MS

  const verifier: StoredVerifier = {
    uid,
    hash,
    iterations: PBKDF2_ITERATIONS,
    expiresAt,
  }

  await runTx(deviceId, STORE_VERIFIERS, 'readwrite', (store) => {
    return store(STORE_VERIFIERS).put(verifier as unknown as Record<string, unknown>)
  })
}

/**
 * Verifies an offline PIN against the stored verifier.
 * Returns:
 * - 'LOCKED': if currently locked out
 * - 'NO_VERIFIER': if no verifier exists for uid
 * - 'EXPIRED': if verifier exists but expired (deletes the expired record)
 * - 'OK': if PIN matches (clears lockout and records recent verification)
 * - 'WRONG': if PIN does not match (increments failed attempts, locks after 5 wrong in 15m)
 */
export async function verifyOfflinePin(
  deviceId: string,
  uid: string,
  pin: string,
  now: number = Date.now(),
): Promise<OfflinePinVerifyResult> {
  // 1. Check lockout
  const lockout = (await runTx(deviceId, STORE_LOCKOUTS, 'readonly', (store) => {
    return store(STORE_LOCKOUTS).get(uid)
  })) as LockoutRecord | undefined

  if (lockout?.lockedUntil && now < lockout.lockedUntil) {
    return 'LOCKED'
  }

  // 2. Fetch verifier
  const verifier = (await runTx(deviceId, STORE_VERIFIERS, 'readonly', (store) => {
    return store(STORE_VERIFIERS).get(uid)
  })) as StoredVerifier | undefined

  if (!verifier) {
    return 'NO_VERIFIER'
  }

  // 3. Expiry check: delete expired verifier
  if (now > verifier.expiresAt) {
    await runTx(deviceId, STORE_VERIFIERS, 'readwrite', (store) => {
      return store(STORE_VERIFIERS).delete(uid)
    })
    return 'EXPIRED'
  }

  // 4. Derive and constant-time compare
  const derivedHash = await derivePinHash(deviceId, uid, pin)
  const isMatch = constantTimeEqual(derivedHash, verifier.hash)

  if (isMatch) {
    // Clear lockout attempts
    await runTx(deviceId, [STORE_LOCKOUTS, STORE_RECENT_VERIFICATIONS], 'readwrite', (store) => {
      const lockStore = store(STORE_LOCKOUTS)
      const recStore = store(STORE_RECENT_VERIFICATIONS)
      lockStore.delete(uid)
      return recStore.put({ uid, verifiedAt: now })
    })
    return 'OK'
  }

  // Wrong PIN: update lockout attempts
  const recentAttempts = (lockout?.attempts ?? []).filter((t) => now - t <= SHARED_DEVICE_LOCKOUT_WINDOW_MS)
  recentAttempts.push(now)

  let lockedUntil: number | undefined
  if (recentAttempts.length >= SHARED_DEVICE_LOCKOUT_MAX_ATTEMPTS) {
    lockedUntil = now + SHARED_DEVICE_LOCKOUT_DURATION_MS
  }

  await runTx(deviceId, STORE_LOCKOUTS, 'readwrite', (store) => {
    return store(STORE_LOCKOUTS).put({
      uid,
      attempts: recentAttempts,
      lockedUntil,
    })
  })

  return 'WRONG'
}

/**
 * Returns the lockout timestamp (ms) if the user is currently locked out offline, or null.
 */
export async function getOfflineLockoutUntil(
  deviceId: string,
  uid: string,
  now: number = Date.now(),
): Promise<number | null> {
  const lockout = (await runTx(deviceId, STORE_LOCKOUTS, 'readonly', (store) => {
    return store(STORE_LOCKOUTS).get(uid)
  })) as LockoutRecord | undefined

  if (lockout?.lockedUntil && now < lockout.lockedUntil) {
    return lockout.lockedUntil
  }
  return null
}

/**
 * Deletes the entire device IndexedDB database on device revoke.
 */
export async function deleteSharedDeviceOfflineStore(deviceId: string): Promise<void> {
  const cached = dbCache.get(deviceId)
  if (cached) {
    try {
      const db = await cached
      db.close()
    } catch {
      // Ignore close error
    }
    dbCache.delete(deviceId)
  }

  // Also delete person cache database on revoke
  try {
    const { deleteSharedDevicePersonCacheDb } = await import('./sharedDevicePersonCache')
    await deleteSharedDevicePersonCacheDb(deviceId)
  } catch {
    // Ignore person cache delete error
  }

  if (typeof indexedDB === 'undefined') return

  const dbName = `shared-device-${deviceId}`
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(dbName)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error ?? new Error('Failed to delete database'))
    req.onblocked = () => resolve()
  })
}

/**
 * Saves the latest online people list (names and initials only) for offline display.
 */
export interface InputPerson {
  uid: string
  name?: string
  displayName?: string
  initials: string
}

export async function saveCachedPeople(
  deviceId: string,
  people: InputPerson[],
  cachedAt: number = Date.now(),
): Promise<void> {
  const stripped: CachedPerson[] = people.map((p) => ({
    uid: p.uid,
    name: p.name || p.displayName || '',
    initials: p.initials,
  }))

  await runTx(deviceId, STORE_META, 'readwrite', (store) => {
    return store(STORE_META).put({
      key: 'cached_people',
      cachedAt,
      people: stripped,
    })
  })
}

/**
 * Retrieves the cached people list.
 */
export async function getCachedPeople(
  deviceId: string,
): Promise<CachedPeopleRecord | null> {
  const record = await runTx(deviceId, STORE_META, 'readonly', (store) => {
    return store(STORE_META).get('cached_people')
  })
  if (!record || !Array.isArray(record.people)) return null
  return {
    cachedAt: record.cachedAt,
    people: record.people,
  }
}

/**
 * Lists cached people with an offlineEligible flag (unexpired verifier AND not locked).
 */
export async function listOfflineEligible(
  deviceId: string,
  now: number = Date.now(),
): Promise<OfflineEligiblePerson[]> {
  const cached = await getCachedPeople(deviceId)
  if (!cached || cached.people.length === 0) return []

  const verifiers = (await runTx(deviceId, STORE_VERIFIERS, 'readonly', (store) => {
    return store(STORE_VERIFIERS).getAll()
  })) as StoredVerifier[]

  const lockouts = (await runTx(deviceId, STORE_LOCKOUTS, 'readonly', (store) => {
    return store(STORE_LOCKOUTS).getAll()
  })) as LockoutRecord[]

  const verifierMap = new Map<string, StoredVerifier>(verifiers.map((v) => [v.uid, v]))
  const lockoutMap = new Map<string, LockoutRecord>(lockouts.map((l) => [l.uid, l]))

  return cached.people.map((p) => {
    const v = verifierMap.get(p.uid)
    const l = lockoutMap.get(p.uid)
    const hasUnexpiredVerifier = Boolean(v && now <= v.expiresAt)
    const isLocked = Boolean(l?.lockedUntil && now < l.lockedUntil)
    return {
      uid: p.uid,
      name: p.name,
      initials: p.initials,
      offlineEligible: hasUnexpiredVerifier && !isLocked,
    }
  })
}

/**
 * Starts an offline session for a verified person.
 * Refuses starting if no OK verifier result occurred in the last 60 seconds.
 */
export async function startOfflineSession(
  deviceId: string,
  uid: string,
  now: number = Date.now(),
): Promise<string> {
  const recent = await runTx(deviceId, STORE_RECENT_VERIFICATIONS, 'readonly', (store) => {
    return store(STORE_RECENT_VERIFICATIONS).get(uid)
  })

  if (!recent || typeof recent.verifiedAt !== 'number') {
    throw new Error('Refused: PIN verification required within last 60 seconds')
  }

  const elapsed = now - recent.verifiedAt
  if (elapsed < 0 || elapsed > VERIFICATION_VALIDITY_WINDOW_MS) {
    throw new Error('Refused: PIN verification required within last 60 seconds')
  }

  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const offlineSessionId = `offline-${uid}-${now}-${randomSuffix}`

  const session: StoredOfflineSession = {
    offlineSessionId,
    uid,
    startedAt: now,
  }

  await runTx(deviceId, STORE_SESSIONS, 'readwrite', (store) => {
    return store(STORE_SESSIONS).put(session as unknown as Record<string, unknown>)
  })

  return offlineSessionId
}

/**
 * Ends an offline session.
 */
export async function endOfflineSession(
  deviceId: string,
  offlineSessionId: string,
  reason: string,
  now: number = Date.now(),
): Promise<void> {
  const session = await getOfflineSession(deviceId, offlineSessionId)
  if (!session) return
  session.endedAt = now
  session.endReason = reason
  await runTx(deviceId, STORE_SESSIONS, 'readwrite', (store) => {
    return store(STORE_SESSIONS).put(session as unknown as Record<string, unknown>)
  })
}

/**
 * Retrieves a stored offline session.
 */
export async function getOfflineSession(
  deviceId: string,
  offlineSessionId: string,
): Promise<StoredOfflineSession | null> {
  const session = await runTx(deviceId, STORE_SESSIONS, 'readonly', (store) => {
    return store(STORE_SESSIONS).get(offlineSessionId)
  })
  return (session as StoredOfflineSession) ?? null
}
